import React from 'react';
import type { PurchaseApproval, PurchaseTimelineEntry } from '../api';
import { formatDateTime } from './format';

const EVENT_LABELS: Record<string, string> = {
  'request.created': 'Solicitação criada', 'request.updated': 'Solicitação atualizada',
  'request.items_replaced': 'Itens atualizados', 'request.attachment_added': 'Anexo adicionado',
  'request.attachment_removed': 'Anexo removido', 'workflow.demand_submitted': 'Solicitação enviada para aprovação',
  'workflow.demand_resubmitted': 'Solicitação reenviada para aprovação',
  'workflow.demand.approved': 'Demanda aprovada', 'workflow.demand.rejected': 'Demanda rejeitada',
  'workflow.demand.returned': 'Demanda devolvida', 'quotation.created': 'Cotação criada',
  'quotation.updated': 'Cotação atualizada', 'quotation.proposal_uploaded': 'Proposta anexada',
  'quotation.submitted': 'Cotação enviada', 'quotation.exception_recorded': 'Exceção de cotação registrada',
  'equalization.created': 'Equalização criada', 'equalization.updated': 'Equalização atualizada',
  'equalization.evidence_uploaded': 'Evidência de equalização anexada', 'selection.completed': 'Proposta selecionada',
  'workflow.award.approved': 'Seleção aprovada', 'workflow.award.rejected': 'Seleção rejeitada',
  'workflow.award.returned': 'Seleção devolvida', 'order.created': 'Pedido de compra criado',
  'payment.created': 'Pagamento criado', 'payment.scheduled': 'Pagamento agendado',
  'payment.proof_uploaded': 'Comprovante anexado', 'payment.paid': 'Pagamento realizado',
  'payment.reconciled': 'Pagamento conciliado', 'payment.cancelled': 'Pagamento cancelado',
  'request.closed': 'Solicitação encerrada',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador', purchasing: 'Compras', manager: 'Gestor', finance: 'Financeiro',
  governance: 'Governança', diretoria: 'Diretoria', collaborator: 'Colaborador',
};

function approvalLabel(value: PurchaseApproval): string {
  const status = value.decision ?? value.status ?? 'pending';
  const labels: Record<string, string> = {
    pending: 'Pendente', pendente: 'Pendente', approved: 'Aprovada', aprovado: 'Aprovada',
    rejected: 'Rejeitada', reprovado: 'Rejeitada', returned: 'Devolvida', devolvido: 'Devolvida',
  };
  return labels[status] ?? 'Em processamento';
}

export function ApprovalTrail({ approvals }: { approvals: PurchaseApproval[] }) {
  if (approvals.length === 0) return <p className="text-sm text-gray-500">Nenhuma etapa de aprovação registrada.</p>;
  return (
    <ol className="space-y-3" aria-label="Etapas de aprovação">
      {approvals.map((approval) => (
        <li key={approval.id} className="rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <strong className="text-sm text-gray-900">{approval.stepLabel ?? approval.stepKey}</strong>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{approvalLabel(approval)}</span>
          </div>
          {approval.justification && <p className="mt-2 text-sm text-gray-700">{approval.justification}</p>}
          <p className="mt-1 text-xs text-gray-500">{approval.actorName ?? 'Aguardando responsável'} · {formatDateTime(approval.decidedAt ?? approval.createdAt)}</p>
        </li>
      ))}
    </ol>
  );
}

export function PurchaseTimeline({ entries }: { entries: PurchaseTimelineEntry[] }) {
  if (entries.length === 0) return <p className="text-sm text-gray-500">Nenhum evento registrado.</p>;
  return (
    <ol className="relative ml-2 space-y-4 border-l border-gray-200 pl-5" aria-label="Linha do tempo da compra">
      {entries.map((entry) => (
        <li key={entry.id} className="relative">
          <span className="absolute -left-[1.55rem] top-1 h-2.5 w-2.5 rounded-full bg-blue-600" aria-hidden />
          <p className="text-sm font-medium text-gray-900">{entry.title ?? EVENT_LABELS[entry.event ?? entry.type ?? ''] ?? 'Atualização do processo'}</p>
          {(entry.description ?? entry.detail) && <p className="mt-1 text-sm text-gray-600">{entry.description ?? entry.detail}</p>}
          <p className="mt-1 text-xs text-gray-500">{entry.actorName ?? ROLE_LABELS[entry.actorRole ?? ''] ?? 'Sistema'} · {formatDateTime(entry.eventAt ?? entry.createdAt)}</p>
        </li>
      ))}
    </ol>
  );
}
